import BuyerAccountAPI from "@/api/buyer-account.api"
import CustomerServicesAPI from "@/api/customer-services.api"
import ESignAPI from "@/api/esign.api"
import RingbaAccountsAPI from "@/api/ringba-accounts.api"
import { requirePermissionSelection } from '@/store/account-permissions'
import {convertCurrencyStringToNumber} from  './campaign.store'

const state = {
  embeddedSigningSessionUrl: '',
  accountUsers: [],
}

const getters = {
  allowedTrafficSources: (state, getters, rootState, rootGetters) => {
    return rootGetters['auth/accountDetails']?.allowedTrafficSources
  },

  allowedCategories: (state, getters, rootState, rootGetters) => {
    return rootGetters['auth/accountDetails']?.allowedCategories
  },

  accountHasRingbaIntegrationCompleted(state, getters, rootState, rootGetters) {
    if (!rootGetters['auth/hasPermission']('admin')) {
      return rootGetters['sellerOffer/canCreateRingTreeTarget'] === true
    }
    const ringbaAccountId = rootGetters['auth/ringbaAccountId']
    const defaultToken = rootGetters['ringbaTokens/defaultToken']

    return ringbaAccountId != null 
          && ringbaAccountId !== '' 
          && defaultToken != null
  }
}

/** 
 * @type {import('vuex').ActionTree<typeof state>} 
 */
const actions = {
  async createOnboardingDocsForSeller({ rootGetters, commit }, successRedirectURL) {
    const response = await ESignAPI.createOnboardingDocsForSeller(rootGetters['auth/accountDetails']?.id, successRedirectURL)

    commit('SET_EMBEDDED_SIGNING_SESSION_URL', response.data.embeddedSessionURL)

    return response.data
  },

  async getPendingDocumentBundleStatusForSeller({ rootGetters, commit, dispatch }) {
    const response = await ESignAPI.getPendingDocumentBundleStatusForSeller(rootGetters['auth/accountDetails']?.id)

    if (response.data.status === 'SHARED') {
      commit('SET_EMBEDDED_SIGNING_SESSION_URL', response.data.embeddedSessionURL)  
    } else if (response.data.status === 'EXECUTED') {
      await dispatch('auth/fetchUsersAccountDetails', null, { root: true })
    }

    return response.data
  },

  async createOnboardingDocsForBuyer({ rootGetters, commit }, successRedirectURL) {
    const response = await ESignAPI.createOnboardingDocsForBuyer(rootGetters['auth/accountDetails']?.id, successRedirectURL)

    commit('SET_EMBEDDED_SIGNING_SESSION_URL', response.data.embeddedSessionURL)

    return response.data
  },

  async getPendingDocumentBundleStatusForBuyer({ rootGetters, commit, dispatch }) {
    const response = await ESignAPI.getPendingDocumentBundleStatusForBuyer(rootGetters['auth/accountDetails']?.id)

    if (response.data.status === 'SHARED') {
      commit('SET_EMBEDDED_SIGNING_SESSION_URL', response.data.embeddedSessionURL)  
    } else if (response.data.status === 'EXECUTED') {
      await dispatch('auth/fetchUsersAccountDetails', null, { root: true })
    }

    return response.data
  },

  async updateBuyerAccountBudgetCap({ rootGetters, dispatch }, budget) {
    const budgetCopy = {
      ...budget,
      dailyBudgetInUSD: convertCurrencyStringToNumber(budget.dailyBudgetInUSD)
    }
    
    await BuyerAccountAPI.updateBuyerAccountBudgetCap(rootGetters['auth/accountId'], budgetCopy)

    await dispatch('auth/fetchUsersAccountDetails', null, { root: true })
  },

  async updateAccountTimezone({ rootGetters, dispatch }, timezone) {
    const timezoneCopy = {      
      timeZoneId: timezone
    }
    
    await CustomerServicesAPI.updateAccountTimezone(rootGetters['auth/accountId'], timezoneCopy)

    await dispatch('auth/fetchUsersAccountDetails', null, { root: true })
  },

  async updateBuyerCreativeSettings({ rootGetters, dispatch }, creativeSettings) {
    await BuyerAccountAPI.updateBuyerCreativeSettings(rootGetters['auth/accountId'], creativeSettings)

    await dispatch('auth/fetchUsersAccountDetails', null, { root: true })
  },

  async getAccountUsers({ rootGetters, commit }) {
    if (!rootGetters['auth/hasPermission']('admin')) return
    const accountId = rootGetters['auth/accountDetails']?.id

    const response = await CustomerServicesAPI.getAccountUsers({accountId: accountId})
    commit('SET_ACCOUNT_USERS', response.data)

    return response.data
  },

  async createAccountUser({ rootGetters }, payload) {
    if (!rootGetters['auth/hasPermission']('admin')) throw new Error('Account admin access required')
    payload = { ...payload, permissions: requirePermissionSelection(payload.permissions) }
    const accountId = rootGetters['auth/accountDetails']?.id

    const response = await CustomerServicesAPI.createAccountUser({accountId: accountId, userData: payload})
    return response.data
  },

  async removeAccountUser({ rootGetters, dispatch }, userId) {    
    if (!rootGetters['auth/hasPermission']('admin')) throw new Error('Account admin access required')
    if (userId?.toLowerCase() === rootGetters['auth/userId']?.toLowerCase()) throw new Error('You cannot remove yourself')
    const accountId = rootGetters['auth/accountDetails']?.id
    const response = await CustomerServicesAPI.removeAccountUser({accountId: accountId, userId: userId})

    if(response.status === 200){
      await dispatch('getAccountUsers')
    }

    return response.data
  },

  /**
   * Sets a member's permissions. Nobody changes their own: the signed-in user is refused before any
   * request is sent, and the server must refuse it too. An admin changes other users; the server
   * protects the final admin.
   *
   * Resolves once the SERVER has taken the change. A failed refresh of the list must not turn a
   * committed change into a reported failure, so it resolves with `refreshFailed` instead.
   * Resolves `{ data, refreshFailed }`; rejects when the change itself was refused.
   */
  async setAccountUserPermissions({ rootGetters, dispatch }, { userId, permissions }) {
    if (userId?.toLowerCase() === rootGetters['auth/userId']?.toLowerCase()) {
      throw new Error('You cannot change your own permissions')
    }
    if (!rootGetters['auth/hasPermission']('admin')) throw new Error('Account admin access required')
    permissions = requirePermissionSelection(permissions)

    const accountId = rootGetters['auth/accountDetails']?.id

    const response = await CustomerServicesAPI.setAccountUserPermissions({ accountId, userId, permissions })

    let refreshFailed = false

    try {
      await dispatch('getAccountUsers')
    } catch (error) {
      refreshFailed = true
      console.error('The permissions were changed, but the list of users could not be refreshed', error)
    }

    return { data: response.data, refreshFailed }
  },

  async setRingbaAccount({ rootGetters, dispatch }, ringbaAccountId) {
    const accountId = rootGetters['auth/accountId']
    const response = await RingbaAccountsAPI.setRingbaAccount(accountId, ringbaAccountId)
    
    // Refresh user account details to get the updated ringbaAccountId
    await dispatch('auth/fetchUsersAccountDetails', null, { root: true })
    
    return response
  },
}

const mutations = {
  SET_EMBEDDED_SIGNING_SESSION_URL(state, url) {
    state.embeddedSigningSessionUrl = url
  },

  SET_ACCOUNT_USERS(state, data) {
    state.accountUsers = data
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}  